'use client';

/**
 * Everything device-local a signed-in customer leaves behind on a shared
 * phone, dropped when they sign out — the same contract the guest assistant's
 * conversation restore already honours (a conversation that no longer exists
 * server-side is deleted, not resurrected).
 *
 * WHAT IS CLEARED, and why each one:
 *
 *  - `mnrq:guest-orders:v1` — the guest-order access tokens saved while
 *    browsing signed-out. Server history lives with the account; the device
 *    copy must not outlive the session, or the next person to pick up the
 *    phone inherits the previous guest's order links.
 *  - `menuraq:menu-cache:account:*` — the You-panel snapshot (loyalty, gift
 *    cards, recent orders). Scoped per business via `clearAccountCache`.
 *  - `mnrq:assistant:*` — one conversation pointer per business.
 *
 * WHAT IS DELIBERATELY KEPT:
 *
 *  - Carts (`mnrq:cart:*`) — a basket is browse state, not identity. A guest
 *    who signs out mid-order should find it intact, and a staff member using
 *    the same browser for their own menu does not want a stranger's cart
 *    deleted either.
 *  - The guest handle (`mnrq:guest-identity:v1`) and reservation requests —
 *    not order data, and the handle is re-claimed on the next sign-in.
 *
 * Every access is best-effort: private mode and locked-down storage degrade to
 * "nothing to clear", never to a throw on the sign-out path.
 */

import { clearGuestOrders } from '@/lib/customer/guest-orders';
import { clearAccountCache } from '@/lib/customer/account-prefetch';

/** Same prefix `CustomerAssistant` writes its per-business conversation to. */
const ASSISTANT_KEY_PREFIX = 'mnrq:assistant:';

function clearAssistantConversations(): void {
  if (typeof window === 'undefined') return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(ASSISTANT_KEY_PREFIX)) doomed.push(key);
    }
    doomed.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    /* best-effort */
  }
}

/** Clear every customer-scoped local artefact. Call from every sign-out path. */
export function clearCustomerLocalData(): void {
  clearGuestOrders();
  clearAccountCache();
  clearAssistantConversations();
}
